<template>
    <div class="mb-[100px]">
        <div class="mb-[27px] mt-[42px] flex h-[42px] justify-between">
            <h1
                class="text-[21px] font-medium leading-normal tracking-small text-gray-800"
            >
                <span class="flex flex-wrap items-center gap-2">
                    <span class="flex items-center gap-2">
                        <span
                            class="h-2 w-2 rounded-full"
                            :class="getDomainStatusDotClass(domainState)"
                        ></span>
                        <span class="text-gray-800">
                            {{ reportedDomain.domain }}
                        </span>
                    </span>
                </span>
            </h1>
        </div>

        <BasicTabs :tabs="tabs" :tab-key="'domain-dmarc-management'" />

        <div v-if="showSubdomainNotice" class="mb-[150px] mt-[18px]">
            <div
                class="flex w-full items-center justify-between gap-[19px] rounded-[6.75px] border border-[#63B3ED] bg-[#EFF6FF] px-[14px] py-[14px]"
            >
                <div class="flex min-w-0 items-center gap-[19px]">
                    <span class="shrink-0" aria-hidden="true">
                        <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                fill-rule="evenodd"
                                clip-rule="evenodd"
                                d="M19 10.5C19 12.7543 18.1045 14.9163 16.5104 16.5104C14.9163 18.1045 12.7543 19 10.5 19C8.24566 19 6.08365 18.1045 4.48959 16.5104C2.89553 14.9163 2 12.7543 2 10.5C2 8.24566 2.89553 6.08365 4.48959 4.48959C6.08365 2.89553 8.24566 2 10.5 2C12.7543 2 14.9163 2.89553 16.5104 4.48959C18.1045 6.08365 19 8.24566 19 10.5ZM8.25 9.75C8.25 9.55109 8.32902 9.36032 8.46967 9.21967C8.61032 9.07902 8.80109 9 9 9H9.253C9.51515 8.99989 9.77398 9.05867 10.0104 9.17201C10.2468 9.28536 10.4547 9.45036 10.6187 9.65483C10.7828 9.8593 10.8988 10.098 10.9582 10.3534C11.0176 10.6087 11.0189 10.8741 10.962 11.13L10.502 13.196C10.4939 13.2326 10.4941 13.2706 10.5027 13.3071C10.5113 13.3436 10.5279 13.3777 10.5514 13.4069C10.575 13.4361 10.6048 13.4596 10.6386 13.4757C10.6725 13.4918 10.7095 13.5001 10.747 13.5H11C11.1989 13.5 11.3897 13.579 11.5303 13.7197C11.671 13.8603 11.75 14.0511 11.75 14.25C11.75 14.4489 11.671 14.6397 11.5303 14.7803C11.3897 14.921 11.1989 15 11 15H10.747C10.4848 15.0001 10.226 14.9413 9.98963 14.828C9.75325 14.7146 9.54535 14.5496 9.38129 14.3452C9.21723 14.1407 9.10121 13.902 9.04179 13.6466C8.98237 13.3913 8.98108 13.1259 9.038 12.87L9.498 10.804C9.5061 10.7674 9.50586 10.7294 9.4973 10.6929C9.48874 10.6564 9.47208 10.6223 9.44856 10.5931C9.42503 10.5639 9.39524 10.5404 9.36139 10.5243C9.32754 10.5082 9.29049 10.4999 9.253 10.5H9C8.80109 10.5 8.61032 10.421 8.46967 10.2803C8.32902 10.1397 8.25 9.94891 8.25 9.75ZM10 7C10.2652 7 10.5196 6.89464 10.7071 6.70711C10.8946 6.51957 11 6.26522 11 6C11 5.73478 10.8946 5.48043 10.7071 5.29289C10.5196 5.10536 10.2652 5 10 5C9.73478 5 9.48043 5.10536 9.29289 5.29289C9.10536 5.48043 9 5.73478 9 6C9 6.26522 9.10536 6.51957 9.29289 6.70711C9.48043 6.89464 9.73478 7 10 7Z"
                                fill="#63B3ED"
                            />
                        </svg>
                    </span>
                    <p
                        class="min-w-0 font-inter text-[12.25px] font-normal not-italic leading-[142%] text-[#000]"
                    >
                        <span>
                            {{
                                $t(
                                    "vue.dmarc_management_subdomain_notice_line_inheriting_line1",
                                )
                            }}
                            <span class="font-semibold">{{
                                parentDomainDisplayName
                            }}</span
                            >.
                            {{
                                $t(
                                    "vue.dmarc_management_subdomain_notice_line_inheriting_line2",
                                )
                            }}
                        </span>
                    </p>
                </div>
                <AtomicButton
                    v-if="parentDomainExistInDmarceye"
                    color="yellow"
                    size="sm"
                    class="inline-flex h-[28px] shrink-0 items-center justify-center rounded-[6px] border font-medium transition duration-100 ease-in-out hover:shadow-button focus:outline-hidden focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed border-transparent bg-[#088BEA] text-white hover:text-gray-100 focus:text-gray-100 focus:ring-[#3182CE] disabled:bg-gray-300 disabled:text-white px-[14px] text-[10.5px] leading-[140%]"
                    @click="visitParentDomain"
                >
                    <template #icon>
                        <PlusCircleIcon class="mr-2 h-4 w-4" />
                    </template>
                    {{ $t("vue.dmarc_management_visit_this_domain") }}
                </AtomicButton>
            </div>

            <!-- NOTICE FOR AUTO ADDED DOMAIN PARKED-->
            <!--            <div-->
            <!--                class="flex w-full items-center justify-between gap-[19px] rounded-[6.75px] border border-[#63B3ED] bg-[#EFF6FF] px-[14px] py-[14px]"-->
            <!--            >-->
            <!--                <div class="flex min-w-0 items-center gap-[19px]">-->
            <!--                    <span class="shrink-0" aria-hidden="true">-->
            <!--                        <svg-->
            <!--                            width="20"-->
            <!--                            height="20"-->
            <!--                            viewBox="0 0 20 20"-->
            <!--                            fill="none"-->
            <!--                            xmlns="http://www.w3.org/2000/svg"-->
            <!--                        >-->
            <!--                            <path-->
            <!--                                fill-rule="evenodd"-->
            <!--                                clip-rule="evenodd"-->
            <!--                                d="M19 10.5C19 12.7543 18.1045 14.9163 16.5104 16.5104C14.9163 18.1045 12.7543 19 10.5 19C8.24566 19 6.08365 18.1045 4.48959 16.5104C2.89553 14.9163 2 12.7543 2 10.5C2 8.24566 2.89553 6.08365 4.48959 4.48959C6.08365 2.89553 8.24566 2 10.5 2C12.7543 2 14.9163 2.89553 16.5104 4.48959C18.1045 6.08365 19 8.24566 19 10.5ZM8.25 9.75C8.25 9.55109 8.32902 9.36032 8.46967 9.21967C8.61032 9.07902 8.80109 9 9 9H9.253C9.51515 8.99989 9.77398 9.05867 10.0104 9.17201C10.2468 9.28536 10.4547 9.45036 10.6187 9.65483C10.7828 9.8593 10.8988 10.098 10.9582 10.3534C11.0176 10.6087 11.0189 10.8741 10.962 11.13L10.502 13.196C10.4939 13.2326 10.4941 13.2706 10.5027 13.3071C10.5113 13.3436 10.5279 13.3777 10.5514 13.4069C10.575 13.4361 10.6048 13.4596 10.6386 13.4757C10.6725 13.4918 10.7095 13.5001 10.747 13.5H11C11.1989 13.5 11.3897 13.579 11.5303 13.7197C11.671 13.8603 11.75 14.0511 11.75 14.25C11.75 14.4489 11.671 14.6397 11.5303 14.7803C11.3897 14.921 11.1989 15 11 15H10.747C10.4848 15.0001 10.226 14.9413 9.98963 14.828C9.75325 14.7146 9.54535 14.5496 9.38129 14.3452C9.21723 14.1407 9.10121 13.902 9.04179 13.6466C8.98237 13.3913 8.98108 13.1259 9.038 12.87L9.498 10.804C9.5061 10.7674 9.50586 10.7294 9.4973 10.6929C9.48874 10.6564 9.47208 10.6223 9.44856 10.5931C9.42503 10.5639 9.39524 10.5404 9.36139 10.5243C9.32754 10.5082 9.29049 10.4999 9.253 10.5H9C8.80109 10.5 8.61032 10.421 8.46967 10.2803C8.32902 10.1397 8.25 9.94891 8.25 9.75ZM10 7C10.2652 7 10.5196 6.89464 10.7071 6.70711C10.8946 6.51957 11 6.26522 11 6C11 5.73478 10.8946 5.48043 10.7071 5.29289C10.5196 5.10536 10.2652 5 10 5C9.73478 5 9.48043 5.10536 9.29289 5.29289C9.10536 5.48043 9 5.73478 9 6C9 6.26522 9.10536 6.51957 9.29289 6.70711C9.48043 6.89464 9.73478 7 10 7Z"-->
            <!--                                fill="#63B3ED"-->
            <!--                            />-->
            <!--                        </svg>-->
            <!--                    </span>-->
            <!--                    <p-->
            <!--                        class="min-w-0 font-inter text-[12.25px] font-normal not-italic leading-[142%] text-[#000]"-->
            <!--                    >-->
            <!--                        <span>-->
            <!--                            {{-->
            <!--                                $t(-->
            <!--                                    "vue.dmarc_management_subdomain_notice_line1",-->
            <!--                                )-->
            <!--                            }}-->
            <!--                            <span class="font-semibold">{{ parentDomain }}</span-->
            <!--                            >.-->
            <!--                        </span>-->
            <!--                        <span class="block">-->
            <!--                            {{-->
            <!--                                $t(-->
            <!--                                    "vue.dmarc_management_subdomain_notice_line2",-->
            <!--                                )-->
            <!--                            }}-->
            <!--                        </span>-->
            <!--                    </p>-->
            <!--                </div>-->
            <!--                <AtomicButton-->
            <!--                    color="yellow"-->
            <!--                    size="sm"-->
            <!--                    class="inline-flex h-[28px] shrink-0 items-center justify-center rounded-[6px] border font-medium transition duration-100 ease-in-out hover:shadow-button focus:outline-hidden focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed border-transparent bg-[#088BEA] text-white hover:text-gray-100 focus:text-gray-100 focus:ring-[#3182CE] disabled:bg-gray-300 disabled:text-white px-[14px] text-[10.5px] leading-[140%]"-->
            <!--                    @click="handleAddDomainWizard"-->
            <!--                >-->
            <!--                    <template #icon>-->
            <!--                        <PlusCircleIcon class="mr-2 h-4 w-4" />-->
            <!--                    </template>-->
            <!--                    {{ $t("vue.dmarc_management_add_this_domain") }}-->
            <!--                </AtomicButton>-->
            <!--            </div>-->
        </div>

        <div
            v-else
            class="mb-[150px] mt-[42px] grid grid-cols-[260px_1fr] gap-12"
        >
            <div class="flex flex-col">
                <div
                    :class="{
                        'mb-[500px]': showDnsRecords,
                        'mb-32': !showDnsRecords,
                    }"
                >
                    <h3 class="text-lg font-semibold text-gray-800">
                        {{ $t("vue.domain_status") }}
                    </h3>
                    <p class="text-sm text-gray-500">
                        {{ $t("vue.basic_info_about_your_domain") }}
                    </p>
                </div>

                <div>
                    <h3 class="text-lg font-semibold text-gray-800">
                        {{ $t("vue.dmarc_configuration") }}
                    </h3>
                    <p class="text-sm text-gray-500">
                        {{ $t("vue.update_your_txt_record") }}
                    </p>
                </div>
            </div>

            <div class="space-y-6">
                <div class="rounded-[12.8px] border border-gray-200 bg-white">
                    <div
                        class="grid grid-cols-3 gap-[21px] px-[21px] py-[21px]"
                    >
                        <div>
                            <div
                                class="text-[12px] font-semibold leading-[140%] text-gray-800"
                            >
                                {{ $t("vue.status") }}
                            </div>
                            <div class="mt-2">
                                <span
                                    class="policy-badge"
                                    :class="domainStatusBadgeClass"
                                >
                                    {{ domainStatusLabel }}
                                </span>
                            </div>
                        </div>

                        <div>
                            <div
                                class="text-[12px] font-semibold leading-[140%] text-gray-800"
                            >
                                {{ $t("vue.dmarc_policy") }}
                            </div>
                            <div class="mt-2">
                                <span
                                    class="policy-badge capitalize"
                                    :class="policyBadgeClass"
                                >
                                    {{ policyDisplayLabel }}
                                </span>
                            </div>
                        </div>

                        <div>
                            <div
                                class="text-[12px] font-semibold leading-[140%] text-gray-800"
                            >
                                {{ $t("vue.checked_at_date") }}
                            </div>
                            <div class="mt-2">
                                <span class="text-sm text-gray-500">
                                    {{ recordStatusCheckedAt }}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div v-if="showDnsRecords">
                        <div
                            class="border-t border-gray-200 px-[21px] py-[21px]"
                        >
                            <div class="flex flex-col gap-[13px]">
                                <div>
                                    <div
                                        class="text-[12px] font-semibold leading-[140%] text-gray-800"
                                    >
                                        DMARC
                                    </div>
                                    <p
                                        class="mt-1 text-[10.5px] leading-[142%] text-gray-500"
                                    >
                                        {{
                                            $t(
                                                "vue.dmarc_management_dmarc_description",
                                            )
                                        }}
                                    </p>
                                </div>
                                <div
                                    class="rounded-md border border-gray-200 bg-gray-50 px-[14px] py-[10px] text-[10.5px] leading-[140%] text-gray-700"
                                >
                                    {{ detectedDmarcRecordDisplay }}
                                </div>
                            </div>
                        </div>

                        <div
                            class="border-t border-gray-200 px-[21px] py-[21px]"
                        >
                            <div class="flex flex-col gap-[13px]">
                                <div>
                                    <div
                                        class="text-[12px] font-semibold leading-[140%] text-gray-800"
                                    >
                                        SPF
                                    </div>
                                    <p
                                        class="mt-1 text-[10.5px] leading-[142%] text-gray-500"
                                    >
                                        {{
                                            $t(
                                                "vue.dmarc_management_spf_description",
                                            )
                                        }}
                                    </p>
                                </div>
                                <div
                                    class="rounded-md border border-gray-200 bg-gray-50 px-[14px] py-[10px] text-[10.5px] leading-[140%] text-gray-700"
                                >
                                    {{ spfRecordDisplay }}
                                </div>
                            </div>
                        </div>

                        <div
                            class="border-t border-gray-200 px-[21px] py-[21px]"
                        >
                            <div class="flex flex-col gap-[13px]">
                                <div>
                                    <div
                                        class="text-[12px] font-semibold leading-[140%] text-gray-800"
                                    >
                                        DKIM
                                    </div>
                                    <p
                                        class="mt-1 text-[10.5px] leading-[142%] text-gray-500"
                                    >
                                        {{
                                            $t(
                                                "vue.dmarc_management_dkim_description",
                                            )
                                        }}
                                    </p>
                                </div>
                                <div
                                    class="rounded-md border border-gray-200 bg-gray-50 px-[14px] py-[10px] text-[10.5px] leading-[140%] text-gray-700"
                                >
                                    {{ dkimSelectorsDisplay }}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="border-t border-gray-200 px-[21px] py-[10px]">
                        <div class="flex items-center justify-end gap-[14px]">
                            <button
                                class="inline-flex cursor-pointer items-center gap-[3.65px] text-[10.5px] font-medium leading-[140%] text-blue-600 hover:text-blue-700"
                                @click="showDnsRecords = !showDnsRecords"
                            >
                                <span>
                                    {{
                                        showDnsRecords
                                            ? $t("vue.hide_dns_records")
                                            : $t("vue.show_dns_records")
                                    }}
                                </span>
                                <component
                                    :is="
                                        showDnsRecords ? ChevronUp : ChevronDown
                                    "
                                    class="h-[14px] w-[9px]"
                                />
                            </button>

                            <button
                                class="relative inline-flex h-[28px] cursor-pointer items-center justify-center gap-[3.65px] rounded-[6px] bg-blue-500 px-[14px] text-[10.5px] font-medium leading-[140%] text-white transition hover:bg-blue-600"
                                :aria-busy="
                                    dnsCheckingInProgress ? 'true' : 'false'
                                "
                                @click="checkDnsRecords"
                            >
                                <span
                                    v-if="showPulse"
                                    :key="pulseKey"
                                    class="pulse-ring pointer-events-none absolute -inset-1 rounded-[6px]"
                                    aria-hidden="true"
                                    @animationend="showPulse = false"
                                />
                                <RefreshCw class="h-[14px] w-[14px]" />
                                {{ $t("vue.reload_dns_records") }}
                            </button>
                        </div>
                    </div>
                </div>

                <div class="rounded-[12.8px] border border-gray-200 bg-white">
                    <div
                        class="flex items-start justify-between gap-6 px-[24px] pt-[18px]"
                    >
                        <div>
                            <h3
                                class="text-[12px] font-semibold text-gray-800 leading-[140%]"
                            >
                                {{ $t("vue.configure_your_txt_record") }}
                            </h3>
                            <p
                                class="mt-1 text-[10.5px] leading-[142%] text-[#4B5563]"
                            >
                                {{ $t("vue.set_your_dmarc_policy") }}
                            </p>
                        </div>

                        <div class="flex items-center gap-[6px]">
                            <span
                                class="rounded-md bg-gray-100 px-[10px] py-[6px] text-[10.5px] font-medium text-gray-700"
                                >DMARC</span
                            >
                            <span
                                class="rounded-md px-[10px] py-[6px] text-[10.5px] text-gray-400"
                                >SPF</span
                            >
                            <span
                                class="rounded-md px-[10px] py-[6px] text-[10.5px] text-gray-400"
                                >DKIM</span
                            >
                            <span
                                class="rounded-md px-[10px] py-[6px] text-[10.5px] text-gray-400"
                                >BIMI</span
                            >
                        </div>
                    </div>

                    <div class="px-[24px] pb-[24px] pt-[18px]">
                        <DmarcRecordConfigurator
                            v-model="txtRecord"
                            host-name="_dmarc"
                        />

                        <DnsProviderSetBlock
                            :domain-name="reportedDomain.domain"
                            :desired-dmarc-record="txtRecord"
                            :detected-dns-provider="detectedDnsProvider"
                            :show-one-click-block="isCloudflare"
                            user-source-destination="dmarc-management"
                        />

                        <EmailInstructions
                            class="mt-[24px]"
                            :domain="reportedDomain.domain"
                            :txt-record="txtRecord"
                            title-key="vue.email_instructions_to_developer"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import axios from "axios";
import route from "ziggy-js";
import { computed, onMounted, reactive, ref, watch, type PropType } from "vue";
import { router } from "@inertiajs/vue3";
import BasicTabs from "@/components/Atomic/Tabs/BasicTabs.vue";
import { ChevronDown, ChevronUp, RefreshCw, Copy } from "lucide-vue-next";
import { create } from "@/composables/useNotifications";
import { useTranslations } from "@/composables/useTranslations";
import { buildDomainReportTabs } from "@/Pages/Reports/utils/buildDomainReportTabs";
import EmailInstructions from "@/components/Special/EmailInstructions.vue";
import AtomicButton from "@/components/Atomic/Buttons/AtomicButton.vue";
import { PlusCircleIcon } from "@heroicons/vue/24/outline";
import {
    getDomainStatusBadgeClass,
    getDomainStatusDotClass,
    getDomainStatusTranslationKey,
} from "@/utils/domainStatus";
import type IDomain from "@/types/Domains/IDomain.ts";

const { t, locale } = useTranslations();

interface ReportedDomain {
    domain: string;
    active: boolean;
    created_at?: string;
    dmarcPolicy: string;
    dmarcRecord?: string | null;
    account: string;
    parked?: boolean;
}

interface DomainInfo {
    dnsProvider?: string | null;
    spfRecord?: string | null;
    dmarcRecord?: string | null;
    dkimSelectors?: string[] | null;
}

const props = defineProps({
    reportedDomain: {
        type: Object as PropType<ReportedDomain>,
        required: true,
    },
    domainInfo: {
        type: Object as PropType<DomainInfo>,
        required: false,
        default: () => ({}),
    },
    isCloudflare: {
        type: Boolean,
        required: false,
        default: true,
    },
    isSubdomainRecord: {
        type: Boolean,
        required: false,
        default: false,
    },
    parentDomain: {
        type: Object as PropType<IDomain | null>,
        required: false,
        default: null,
    },
    parentDomainName: {
        type: String as PropType<string | null>,
        required: false,
        default: null,
    },
    parentDomainExistInDmarceye: {
        type: Boolean,
        required: false,
        default: false,
    },
});

const tabs = computed(() =>
    buildDomainReportTabs(
        props.reportedDomain.domain,
        props.reportedDomain?.parked ?? false,
        t,
    ),
);

const domainState = reactive({
    active: false,
    parked: false,
    dmarcPolicy: "none",
    dmarcRecord: null as string | null,
});

const policy = ref(props.reportedDomain.dmarcPolicy?.toString() || "none");
const detectedDmarcRecordRaw = ref<string | null>(null);
const detectedSpfRecordRaw = ref<string | null>(
    props.domainInfo?.spfRecord ?? null,
);
const detectedDkimSelectors = ref<string[]>(
    props.domainInfo?.dkimSelectors ?? [],
);
const detectedDmarcRecordDisplay = ref<string[]>(
    props.domainInfo?.dmarcRecord ?? t("vue.not_detected"),
);

const dnsCheckingInProgress = ref(false);
const showDnsRecords = ref(false);
const recordStatusCheckedAt = ref("-");

const pulseKey = ref(0);
const showPulse = ref(false);

onMounted(() => {
    domainState.active = props.reportedDomain.active;
    domainState.parked = props.reportedDomain.parked ?? false;
    domainState.dmarcPolicy = props.reportedDomain.dmarcPolicy;
    domainState.dmarcRecord = props.reportedDomain.dmarcRecord ?? null;
    recordStatusCheckedAt.value = formatRecordStatusCheckedAt(new Date());
});

const domainStatusBadgeClass = computed(() => {
    return getDomainStatusBadgeClass(domainState);
});

const domainStatusLabel = computed(() => {
    return t(getDomainStatusTranslationKey(domainState));
});

const generatedTxtRecord = computed(
    () =>
        `v=DMARC1; p=${policy.value}; pct=100; rua=mailto:${props.reportedDomain.account}@rua.dmarceye.com; fo=1; ri=86400`,
);

const baseTxtRecord = computed(() => {
    if (detectedDmarcRecordRaw.value) {
        return applyPolicyToDmarcRecord(
            detectedDmarcRecordRaw.value,
            policy.value,
        );
    }

    const dmarcRecord = props.reportedDomain.dmarcRecord;

    if (dmarcRecord === "" || dmarcRecord === null) {
        return generatedTxtRecord.value;
    }

    return applyPolicyToDmarcRecord(dmarcRecord, policy.value);
});

const txtRecord = ref(baseTxtRecord.value);
const lastBaseTxtRecord = ref(baseTxtRecord.value);

watch(
    () => baseTxtRecord.value,
    (value) => {
        if (txtRecord.value === lastBaseTxtRecord.value) {
            txtRecord.value = value;
        }

        lastBaseTxtRecord.value = value;
    },
    { immediate: true },
);

const spfRecordDisplay = computed(() =>
    normalizeDnsValue(
        detectedSpfRecordRaw.value ?? props.domainInfo?.spfRecord,
    ),
);

const dkimSelectorsDisplay = computed(() => {
    const selectors = detectedDkimSelectors.value.length
        ? detectedDkimSelectors.value
        : (props.domainInfo?.dkimSelectors ?? []);

    if (selectors.length === 0) {
        return t("vue.not_detected");
    }

    return t("vue.dkim_selectors_found", {
        selectors: selectors.join(", "),
    });
});

const detectedDnsProvider = computed(() =>
    (props.domainInfo?.dnsProvider || "").trim(),
);

const policyDisplay = computed(() => {
    const currentPolicy = domainState.dmarcPolicy?.trim() || "";

    if (currentPolicy === "") {
        return t("vue.none");
    }

    return currentPolicy;
});

const policyDisplayLabel = computed(() => {
    const policy = policyDisplay.value.toLowerCase();

    if (policy === "reject") return "reject";
    if (policy === "quarantine") return "quarantine";
    if (policy === "none") return "none";

    return policyDisplay.value;
});

const policyBadgeClass = computed(() => {
    if (policyDisplay.value === "reject") {
        return "policy-reject";
    }

    if (policyDisplay.value === "quarantine") {
        return "policy-quarantine";
    }

    return "policy-none";
});

const hasDmarcRecord = computed(() => {
    const record = domainState.dmarcRecord;
    return record !== null && String(record).trim() !== "";
});

const showSubdomainNotice = computed(
    () => props.isSubdomainRecord === true && hasDmarcRecord.value === false,
);

const parentDomainDisplayName = computed(() => {
    return props.parentDomain?.domain ?? props.parentDomainName ?? "";
});

// const handleAddDomainWizard = () => {
//     router.get(
//         route("domainGuide.index", { domain: props.reportedDomain.domain }),
//     );
// };

const visitParentDomain = () => {
    if (props.parentDomain === null) {
        return;
    }

    router.get(
        route("reports.dmarc-management.show", {
            domain: props.parentDomain.domain,
        }),
    );
};

function triggerPulse() {
    pulseKey.value += 1;
    showPulse.value = true;
}

async function checkDnsRecords() {
    triggerPulse();

    if (dnsCheckingInProgress.value) {
        return;
    }

    dnsCheckingInProgress.value = true;

    try {
        const response = await axios.get(
            route("domains.check-dns", { domain: props.reportedDomain.domain }),
        );

        if (!response.data.success) {
            console.warn("DNS checking failed:", response.data.error);
            return;
        }

        const result = response.data.result;

        domainState.active = result.active;
        domainState.dmarcPolicy = result.dmarc_policy;
        domainState.dmarcRecord = result.dmarc_record ?? null;

        if (result.dmarc_policy) {
            policy.value = result.dmarc_policy;
        }

        detectedDmarcRecordDisplay.value = result.dmarc_record ?? null;
        detectedSpfRecordRaw.value = result.spf_record ?? null;
        detectedDkimSelectors.value = Array.isArray(result.dkim_selectors)
            ? result.dkim_selectors
            : [];
        recordStatusCheckedAt.value = formatRecordStatusCheckedAt(new Date());

        create({
            title: t("vue.record_checked"),
            type: "success",
        });
    } catch (error) {
        console.error("Failed to check DNS records:", error);
    } finally {
        dnsCheckingInProgress.value = false;
    }
}

async function copyRecord() {
    try {
        if (navigator.clipboard) {
            await navigator.clipboard.writeText(txtRecord.value);
        } else {
            const el = document.createElement("textarea");
            el.value = txtRecord.value;
            document.body.appendChild(el);
            el.select();
            document.execCommand("copy");
            document.body.removeChild(el);
        }

        create({
            title: t("vue.copied_to_clipboard"),
            type: "success",
        });
    } catch (error) {
        console.error("Failed to copy DMARC record:", error);
    }
}

function applyPolicyToDmarcRecord(record: string, newPolicy: string) {
    if (!record) {
        return record;
    }

    if (/;\s*p\s*=/i.test(record) || /\bp\s*=/i.test(record)) {
        return record.replace(
            /(\bp\s*=\s*)(none|quarantine|reject)\b/i,
            `$1${newPolicy}`,
        );
    }

    return record.replace(/v\s*=\s*DMARC1\s*;?/i, (match) => {
        const hasSemicolon = match.trim().endsWith(";");
        return hasSemicolon
            ? `${match} p=${newPolicy};`
            : `${match}; p=${newPolicy};`;
    });
}

function normalizeDnsValue(value?: string | null): string {
    if (!value) {
        return t("vue.not_detected");
    }

    const normalized = value.trim();

    if (normalized === "") {
        return t("vue.not_detected");
    }

    return normalized;
}

function formatRecordStatusCheckedAt(date: Date): string {
    return new Intl.DateTimeFormat(locale.value, {
        dateStyle: "medium",
        timeStyle: "short",
    }).format(date);
}
</script>

<style scoped>
.pulse-ring {
    color: rgb(147 197 253 / 0.85);
    box-shadow: 0 0 0 2px currentColor;
    animation: ringPulse 900ms ease-out both;
    transform-origin: center;
}

@keyframes ringPulse {
    0% {
        transform: scale(0.98);
        opacity: 0;
    }
    15% {
        opacity: 1;
    }
    100% {
        transform: scale(1.08);
        opacity: 0;
    }
}
</style>
